// Which conversation items arrived after the conversation first rendered, so they can fade in
// once. Initial history, older pages loaded by "Read older" (they sit before everything
// already shown), and re-renders of existing items never count.

import { useEffect, useRef } from 'react';

import type { ThreadItem } from './inline.js';

export function useNewItems(
  items: readonly ThreadItem[],
  ready: boolean,
  resetKey: string,
): ReadonlySet<string> {
  const seen = useRef<Set<string>>(new Set());
  const fresh = useRef<Set<string>>(new Set());
  const started = useRef(false);
  const lastReset = useRef(resetKey);

  if (lastReset.current !== resetKey) {
    lastReset.current = resetKey;
    seen.current = new Set();
    fresh.current = new Set();
    started.current = false;
  }

  if (started.current) {
    const firstSeen = items.findIndex((item) => seen.current.has(item.key));
    items.forEach((item, index) => {
      if (!seen.current.has(item.key) && index > firstSeen) {
        fresh.current.add(item.key);
      }
    });
  }

  useEffect(() => {
    if (!ready) {
      return;
    }
    items.forEach((item) => seen.current.add(item.key));
    started.current = true;
  });

  return fresh.current;
}
